import React, { useState, useMemo, } from 'react';
import { params, shape, arrayOf, string as stringType, number as numberType, useDataModel, } from '@thd-nucleus/data-sources';
import { useIsMounted } from '../hooks/use-is-mounted';
import { addManageUsersApi } from '../api/b2b/add-manage-user';
import { defaultSortBy, defaultFilterConfig, } from '../models/b2b/add-manage-users';
import { setUserPageErrorAnalytics } from '../utils/add-manage-user-analytics';
import { getApiError } from '../api/axios-util';
import { useCustomerInfo } from '../hooks/use-customer-info';
var AddManageUsersContext = React.createContext(null);
var AddManageUsersProvider = function (_a) {
    var children = _a.children;
    var _b = useState(null), users = _b[0], setUsers = _b[1];
    var _c = useState(null), paginationInfo = _c[0], setPaginationInfo = _c[1];
    var _d = useState(1), currentPageNumber = _d[0], setCurrentPageNumber = _d[1];
    var _e = useState(null), alertMessage = _e[0], setAlertMessage = _e[1];
    var _f = useState(null), editableUser = _f[0], setEditableUser = _f[1];
    var _g = useState(null), formMode = _g[0], setFormMode = _g[1];
    var _h = useState({
        sortBy: defaultSortBy,
        ascendingOrder: false,
    }), currentSortByOptions = _h[0], setCurrentSortByOptions = _h[1];
    var _j = useState(defaultFilterConfig), currentSearchByValues = _j[0], setCurrentSearchByValues = _j[1];
    var _k = useState(false), showSearchResult = _k[0], setShowSearchResult = _k[1];
    var _l = useState(null), policy = _l[0], setPolicy = _l[1];
    var isMounted = useIsMounted();
    var _m = useCustomerInfo(), customerAccountId = _m.svocID, userId = _m.userID;
    var getUsers = function (sortConfig, filterConfig) {
        return addManageUsersApi
            .getUsers(sortConfig, filterConfig)
            .then(function (_a) {
            var data = _a.data;
            if (isMounted) {
                setUsers(data.users);
                setPaginationInfo(data.paginationInfo);
            }
        })
            .catch(function (error) {
            var _a = getApiError(error, 'users'), errorCode = _a.errorCode, errorMessage = _a.errorMessage;
            setUserPageErrorAnalytics('error', 'page', 'add manage users', errorMessage, errorCode || 'n/a');
            throw error;
        });
    };
    var getPolicy = function () {
        return addManageUsersApi
            .getPolicy()
            .then(function (_a) {
            var data = _a.data;
            if (isMounted) {
                setPolicy(data);
            }
        })
            .catch(function (error) {
            throw error;
        });
    };
    var data = useDataModel('loyaltyMembershipDetails', {
        variables: { userId: userId, customerAccountId: customerAccountId },
        ssr: false,
        skip: !userId || !customerAccountId,
        context: { withAuth: true },
    }).data;
    var isBulkAddUserAllowed = useMemo(function () {
        var _a, _b, _c;
        if (!data)
            return false;
        return !!((_c = (_b = (_a = data.loyaltyMembershipDetails) === null || _a === void 0 ? void 0 : _a.proTiers) === null || _b === void 0 ? void 0 : _b.benefitTags) === null || _c === void 0 ? void 0 : _c.find(function (_a) {
            var tagName = _a.tagName;
            return tagName === 'BulkUser';
        }));
    }, [data]);
    var isPartOfRunnerAllowance = useMemo(function () {
        var _a, _b, _c;
        if (!data)
            return false;
        return !!((_c = (_b = (_a = data.loyaltyMembershipDetails) === null || _a === void 0 ? void 0 : _a.proTiers) === null || _b === void 0 ? void 0 : _b.benefitTags) === null || _c === void 0 ? void 0 : _c.find(function (_a) {
            var tagName = _a.tagName;
            return tagName === 'runnerAllowance';
        }));
    }, [data]);
    var hasProTradeCredit = useMemo(function () {
        var _a, _b, _c;
        if (!data)
            return false;
        return !!((_c = (_b = (_a = data.loyaltyMembershipDetails) === null || _a === void 0 ? void 0 : _a.proTiers) === null || _b === void 0 ? void 0 : _b.benefitTags) === null || _c === void 0 ? void 0 : _c.find(function (_a) {
            var tagName = _a.tagName;
            return tagName === 'ProTradeCredit';
        }));
    }, [data]);
    return (React.createElement(AddManageUsersContext.Provider, { value: {
            users: users,
            paginationInfo: paginationInfo,
            alertMessage: alertMessage,
            currentPageNumber: currentPageNumber,
            currentSortByOptions: currentSortByOptions,
            currentSearchByValues: currentSearchByValues,
            formMode: formMode,
            editableUser: editableUser,
            showSearchResult: showSearchResult,
            isBulkAddUserAllowed: isBulkAddUserAllowed,
            isPartOfRunnerAllowance: isPartOfRunnerAllowance,
            hasProTradeCredit: hasProTradeCredit,
            setCurrentSortByOptions: setCurrentSortByOptions,
            setCurrentSearchByValues: setCurrentSearchByValues,
            setCurrentPageNumber: setCurrentPageNumber,
            setShowSearchResult: setShowSearchResult,
            setAlertMessage: setAlertMessage,
            setEditableUser: setEditableUser,
            setFormMode: setFormMode,
            getUsers: getUsers,
            getPolicy: getPolicy,
            policy: policy,
        } }, children));
};
AddManageUsersProvider.dataModel = {
    loyaltyMembershipDetails: params({
        userId: stringType().isRequired(),
        customerAccountId: stringType().isRequired(),
        channelId: numberType().isRequired(),
    }).shape({
        proTiers: shape({
            benefitTags: arrayOf(shape({
                tagName: stringType(),
                value: stringType(),
            })),
        }),
    }),
};
export { AddManageUsersProvider, AddManageUsersContext };
